import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../config';
import { notifySessionExpired } from './sessionExpiry';

// Bump trigger: 22

const developerApi = axios.create({
  baseURL: API_CONFIG.developerApiBase,
});

developerApi.interceptors.request.use(async (config) => {
  const { tokens } = await fetchAuthSession();
  const token = tokens?.idToken?.toString();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Response error handler: an expired Cognito session makes the developer API
 * return 401. Redirect to a clean re-login instead of surfacing raw 401 text.
 * Exported so the 401→redirect wiring is directly unit-testable. Always
 * rethrows so callers still see the rejection (e.g. ApiErrorAlert).
 */
export function handleDeveloperApiError(error) {
  if (error?.response?.status === 401) notifySessionExpired();
  return Promise.reject(error);
}

developerApi.interceptors.response.use((response) => response, handleDeveloperApiError);

// Keys
export const listKeys = (tenantId) =>
  developerApi.get('/developer/keys', { params: { tenantId } }).then((r) => r.data);

export const createKey = (body) =>
  developerApi.post('/developer/keys', body).then((r) => r.data);

export const revokeKey = (keyId) =>
  developerApi.delete(`/developer/keys/${keyId}`).then((r) => r.data);

// Domains
export const listDomains = () =>
  developerApi.get('/developer/domains').then((r) => r.data);

export const registerDomain = (body) =>
  developerApi.post('/developer/domains', body).then((r) => r.data);

export const verifyDomain = (id) =>
  developerApi.post(`/developer/domains/${id}/verify`).then((r) => r.data);

export const deleteDomain = (id) =>
  developerApi.delete(`/developer/domains/${id}`).then((r) => r.data);

// Webhooks
export const listWebhooks = (tenantId) =>
  developerApi.get('/developer/webhooks', { params: { tenantId } }).then((r) => r.data);

export const createWebhook = (body) =>
  developerApi.post('/developer/webhooks', body).then((r) => r.data);

export const updateWebhook = (id, body) =>
  developerApi.put(`/developer/webhooks/${id}`, body).then((r) => r.data);

export const deleteWebhook = (id) =>
  developerApi.delete(`/developer/webhooks/${id}`).then((r) => r.data);

// API Logs — tenant-wide account activity via the OWNER-gated /developer/logs
// dev-API route (ADR-0060 / #582).
//
// IMPORTANT: read logs through THIS developer-API route, NOT the partner API's
// GET /v1/admin/logs. The dev-portal's partner bearer is an st_* pinned to a
// single context, so the partner route is context-CONFINED — it can only ever
// return that one context's logs, never the tenant-wide, cross-context view an
// owner needs. /developer/logs is owner-gated
// (Cognito), resolves the tenant from the account owner's session, and queries
// CloudWatch across ALL of the owner's app contexts. `contextId` here is an
// OPTIONAL filter (omit ⇒ every context), not a confinement. `env` ('live' |
// 'test') selects the tenant via `?tenant=`. Mirrors ui/admin-app
// developerApi.ts getAdminLogs (re-pointed in #574 / ADR-0060). This restores
// the tenant-wide developer-logs surface lost when /developer/logs/api migrated
// to /v1/admin/logs.
export const getAdminLogs = (env, params) =>
  developerApi.get('/developer/logs', { params: { tenant: env, ...params } }).then((r) => r.data);

// Trigger Failures (#1187) — account-wide trigger execution failures, across every
// app context, via the OWNER-gated /developer/trigger-failures dev-API route.
//
// IMPORTANT: read trigger failures through THIS developer-API route, NOT the partner
// API's GET /v1/trigger-failures. The dev-portal's partner bearer is an st_* pinned to
// a single context, so the partner route is context-CONFINED — the exact confinement
// mistake #582/ADR-0060 already paid down for API logs. `contextId` here is an OPTIONAL
// filter (omit ⇒ every context), not a confinement. `env` ('live' | 'test') selects the
// tenant via `?tenant=`. The response carries `{ data, nextCursor, incomplete,
// contextsNotSearched }` — the last two name any context this page could not read (a
// transient fault); a caller MUST surface them, never let an honest gap read as
// "nothing failed". Results are newest-first WITHIN a context; contexts are walked in
// a fixed deterministic order, not merged into one account-wide timeline — see the
// route's own doc for why (a cursor whose size doesn't grow with the account's context
// count).
export const getTriggerFailures = (env, params) =>
  developerApi
    .get('/developer/trigger-failures', { params: { tenant: env, ...params } })
    .then((r) => r.data);

// Webhook deliveries
export const getWebhookDeliveries = (webhookId, params) =>
  developerApi.get(`/developer/webhooks/${webhookId}/deliveries`, { params }).then((r) => r.data);

export const retryWebhookDelivery = (webhookId, deliveryId) =>
  developerApi.post(`/developer/webhooks/${webhookId}/deliveries/${deliveryId}/retry`).then((r) => r.data);

// Scoped token — short-lived st_* for the Scalar API reference playground.
// Never expose sk_live_* or sk_test_* in the browser; this is the only credential
// that reaches the frontend. `tenant` ('live' | 'test') selects which tenant the
// token is scoped to (omitted ⇒ server defaults to test); `ttl` (60-3600s) sets
// the lifetime (omitted ⇒ server defaults to 3600s). Undefined params are not
// serialized by axios, so getScopedToken() preserves the original test/1hr call.
export const getScopedToken = (tenant, ttl) =>
  developerApi.get('/developer/scoped-token', { params: { tenant, ttl } }).then((r) => r.data);

// App contexts — /developer/app-contexts (OWNER-gated control plane, ADR-0049 / #332).
//
// IMPORTANT: list contexts via THIS developer-API route, NOT the partner API's
// GET /v1/app-contexts. The dev-portal's partner-API bearer is an st_* pinned to
// a single context, so it is context-confined and the partner list returns ONLY
// that one context — an OWNER cannot enumerate their own data
// contexts through it. This server-side, OWNER-gated route resolves the tenant
// directly and returns all of them. Likewise, context CREATION is a control-plane
// provisioning act the browser cannot self-authorize (no provisioning:c token
// ever reaches the client), so it goes through this route too. `tenant` is
// 'live' | 'test' (defaults to 'test' on the server when absent). See #324.

// Page size + drain ceiling — mirrors partnerApi.js's own drainList (same
// reasoning: see that file's comment, or ui/admin-app/src/lib/drainPages.ts
// for the full "why 100, why maxPages+1" writeup). A separate copy because
// this file has its own axios instance and dev-portal has no shared lib/
// home to hang a single implementation off of.
const LIST_PAGE_SIZE = 100;
const LIST_MAX_PAGES = 50;

// Termination is on any FALSY nextCursor (null/undefined/''), not strictly
// null/absent — see partnerApi.js's own drainList doc comment for the full
// reasoning (a blank-string cursor is folded into the terminal case, not
// treated as a real resume position).
async function drainList(fetchPage, maxPages = LIST_MAX_PAGES) {
  const all = [];
  let startFrom;
  for (let page = 0; page <= maxPages; page++) {
    // Guard against a null/undefined body (e.g. a 204) — destructuring
    // straight off fetchPage's result would throw instead of degrading to
    // an empty, terminal page.
    const { data, nextCursor } = (await fetchPage(startFrom)) ?? {};
    if (data) all.push(...data);
    if (!nextCursor) return all;
    startFrom = nextCursor;
  }
  throw new Error(
    `drainList: listing still not exhausted after ${maxPages + 1} requests ` +
      `(${all.length} rows read). Refusing to return a partial result.`,
  );
}

// Drains every page: an OWNER's full app-context enumeration must be
// COMPLETE — the wizard's context picker would otherwise silently truncate
// to the first page (server default + this call's own requested limit: 100)
// for any tenant with more contexts than that. (The #324 empty-state check
// itself isn't at risk the same way — a truncated first page still has ≥1
// row whenever the tenant genuinely has any contexts at all; only the
// picker's completeness was the real gap.)
export const listAppContexts = (tenant) =>
  drainList((startFrom) =>
    developerApi
      .get('/developer/app-contexts', { params: { tenant, startFrom, limit: LIST_PAGE_SIZE } })
      .then((r) => (Array.isArray(r.data) ? { data: r.data, nextCursor: null } : r.data)),
  );

// body: { contextId, name?, description? }. Idempotent by contextId (200 if it
// already exists, 201 if created).
export const createAppContext = (tenant, body) =>
  developerApi.post('/developer/app-contexts', body, { params: { tenant } }).then((r) => r.data);

// Compliance retention — /developer/compliance-retention (OWNER-gated, ADR-0035 §D1 / #419).
//
// `tenant` is 'live' | 'test'. `contextId` is OPTIONAL: omitted reads/writes the tenant-wide
// default; present reads/writes that one context's override (the context must already exist
// for the PATCH to succeed — see DeveloperComplianceRetentionHandler). Response/request shape:
// { tenantDefaultDays, tenantDefaultDisposition, [contextId, contextOverrideDays,
// contextOverrideDisposition], effectiveDays, effectiveDisposition }.
//
// PATCH sets BOTH fields on every call — there is no partial-update-if-present semantics. Callers
// must GET first and PATCH back the full desired state; pass `null` for either field to clear it
// to the next level's default (tenant default, or the platform default when clearing the tenant
// level itself).
export const getComplianceRetention = (tenant, contextId) =>
  developerApi
    .get('/developer/compliance-retention', { params: { tenant, contextId } })
    .then((r) => r.data);

export const setComplianceRetention = (tenant, contextId, body) =>
  developerApi
    .patch('/developer/compliance-retention', body, { params: { tenant, contextId } })
    .then((r) => r.data);

// Peer-invites — POST /developer/peer-invite (ADR-0053 / #315). An activated
// owner issues (or refreshes) their single redeemable invite link; the response
// carries { inviteId, redeemLink, inviteToken, expiresAt, remaining }. redeemUrl
// is the absolute base the redeem link should point at (the portal's own /redeem).
export const issuePeerInvite = (redeemUrl) =>
  developerApi.post('/developer/peer-invite', redeemUrl ? { redeemUrl } : {}).then((r) => r.data);

// Legal
export const getLegalStatus = () =>
  developerApi.get('/developer/legal/status').then((r) => r.data);

export const acceptTos = () =>
  developerApi.post('/developer/legal/accept-tos').then((r) => r.data);

export const signBaa = () =>
  developerApi.post('/developer/legal/sign-baa').then((r) => r.data);

export const getBaaRecord = () =>
  developerApi.get('/developer/legal/baa-record').then((r) => r.data);

// Data residency & region (ADR-0046 / #260). Per-tenant global-processing waiver.
// `tenant` is 'live' | 'test'. signGlobalProcessingWaiver body: { defaultGlobal, allowOverride }.
export const signGlobalProcessingWaiver = (tenant, body) =>
  developerApi
    .post('/developer/legal/sign-global-processing-waiver', body, { params: { tenant } })
    .then((r) => r.data);

export const disableGlobalProcessing = (tenant) =>
  developerApi
    .post('/developer/legal/disable-global-processing', null, { params: { tenant } })
    .then((r) => r.data);

export const getGlobalWaiverRecord = (tenant) =>
  developerApi
    .get('/developer/legal/global-waiver-record', { params: { tenant } })
    .then((r) => r.data);

// Billing
export const getBilling = () =>
  developerApi.get('/developer/billing').then((r) => r.data);

export const createCheckoutSession = (body) =>
  developerApi.post('/developer/billing/checkout', body).then((r) => r.data);

export const getCheckoutStatus = (sessionId) =>
  developerApi.get('/developer/billing/checkout-status', { params: { sessionId } }).then((r) => r.data);

export const updateOverage = (body) =>
  developerApi.post('/developer/billing/overage', body).then((r) => r.data);

// Inference balance — separate pre-paid ledger from monthly plan credits.
// See backend DeveloperBillingHandler /inference, /inference/fund, /inference/auto-recharge.
export const getInferenceBalance = () =>
  developerApi.get('/developer/billing/inference').then((r) => r.data);

export const fundInferenceBalance = (amountCents) =>
  developerApi.post('/developer/billing/inference/fund', { amountCents }).then((r) => r.data);

export const updateInferenceAutoRecharge = (body) =>
  developerApi.put('/developer/billing/inference/auto-recharge', body).then((r) => r.data);

export default developerApi;
